import React from "react";
import { Link, useLocation } from "react-router-dom";
import { PhoneCall, Mail, Shield, ArrowUpRight, MessageCircle, Send as SendIcon } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { COMPANY } from "@/lib/constants";
import { LOGO_URL } from "@/lib/constants";
import { Marquee } from "@/components/motion-primitives";

export default function Footer() {
  const loc = useLocation();
  if (loc.pathname.startsWith("/admin")) return null;

  return (
    <footer className="relative bg-ink text-white" data-testid="site-footer">
      {/* Big lime marquee band */}
      <div className="bg-lime-pop text-ink py-5 border-y border-ink">
        <Marquee>
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="font-display text-2xl sm:text-3xl font-medium flex items-center gap-8"
            >
              <span>Answer more calls.</span>
              <span className="opacity-40">◆</span>
              <span>Route with intent.</span>
              <span className="opacity-40">◆</span>
              <span>Deploy in days.</span>
              <span className="opacity-40">◆</span>
            </div>
          ))}
        </Marquee>
      </div>

      <div className="container-x py-16 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <div className="flex items-center gap-3">
              <img
                src={LOGO_URL}
                alt="Mavericks"
                className="h-14 w-14 object-contain"
                style={{ filter: "url(#knockout-white)" }}
              />
              <div className="leading-tight">
                <div className="font-display text-xl">Mavericks</div>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">
                  PBX Solutions
                </div>
              </div>
            </div>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              Enterprise-grade PBX phone systems and unified communications —
              engineered so your teams can focus on customers, not call routing.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 group text-white"
            >
              <span className="link-underline pb-1 font-display text-2xl sm:text-3xl">
                Start a conversation
              </span>
              <ArrowUpRight className="h-6 w-6 transition-transform group-hover:rotate-45 group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.22em] text-lime-pop font-semibold mb-5">
              Navigate
            </div>
            <ul className="space-y-3 text-sm">
              {[
                ["/", "Home"],
                ["/services", "Services"],
                ["/about", "About"],
                ["/contact", "Contact"],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link to={to} className="text-white/80 hover:text-white">
                    {label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/admin/login"
                  className="text-white/50 hover:text-lime-pop inline-flex items-center gap-1.5"
                  data-testid="footer-admin-link"
                >
                  <Shield className="h-3.5 w-3.5" /> Admin
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.22em] text-lime-pop font-semibold mb-5">
              Reach us
            </div>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <PhoneCall className="h-4 w-4 mt-0.5 text-lime-pop" />
                <a
                  href={`tel:+${COMPANY.phoneRaw}`}
                  className="text-white hover:text-lime-pop"
                  data-testid="footer-phone"
                >
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MessageCircle className="h-4 w-4 mt-0.5 text-lime-pop" />
                <a
                  href={`https://wa.me/${COMPANY.whatsappRaw}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-lime-pop"
                  data-testid="footer-whatsapp"
                >
                  WhatsApp · {COMPANY.whatsapp}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <SendIcon className="h-4 w-4 mt-0.5 text-lime-pop" />
                <a
                  href={COMPANY.telegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-lime-pop"
                  data-testid="footer-telegram"
                >
                  Telegram · {COMPANY.telegram}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-lime-pop" />
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-white hover:text-lime-pop break-all"
                  data-testid="footer-email"
                >
                  {COMPANY.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-4">
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-5 text-xs text-white/50 font-mono">
            <Link
              to="/privacy"
              className="hover:text-lime-pop transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <span className="opacity-30">·</span>
            <Link
              to="/terms"
              className="hover:text-lime-pop transition-colors"
              data-testid="footer-terms-link"
            >
              Terms
            </Link>
            <span className="opacity-30">·</span>
            <span>Hosted PBX · On-prem</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
